import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, X } from "lucide-react";
import { createClient, updateClient } from "@/lib/clientsApi";
import { createLegacyClient, updateLegacyClient } from "@/lib/legacyClientsApi";
import { fetchPartners } from "@/lib/partnersApi";
import { finalClientTypeOptions } from "@/components/prospects/prospectConstants";
import { COUNTRY_OPTIONS } from "@/lib/countries";

const clientSchemaBase = z.object({
    first_name: z.string().min(1, "First name is required.").max(100),
    last_name: z.string().min(1, "Last name is required.").max(100),
    company: z.string().max(200).nullable().optional(),
    group_name: z.string().max(200).nullable().optional(),
    partner_id: z.number().nullable().optional(),
    country: z.string().max(100).nullable().optional().or(z.literal("")),
    email: z
      .string()
      .email("Invalid email")
      .max(255)
      .nullable()
      .optional()
      .or(z.literal("")),
    mobile: z.string().max(50).nullable().optional().or(z.literal("")),
    alternate_phone: z.string().max(50).nullable().optional(),
    operator_agency_name: z.string().max(200).nullable().optional(),
    referred_from: z
      .enum(["google", "instagram", "facebook", "referral", "other"]) 
      .nullable()
      .optional(),
    client_types: z.array(z.string().max(50)).optional(),
    language: z.enum(["en", "pt_br"], { required_error: "Language is required." }),
    currency: z.enum(["usd", "aed"]).optional(),
    notes: z.string().nullable().optional(),
  });

const buildClientSchema = (requireContact: boolean) => {
  if (!requireContact) {
    return clientSchemaBase;
  }
  return clientSchemaBase.superRefine((data, ctx) => {
    const email = (data.email ?? "").toString().trim();
    const mobile = (data.mobile ?? "").toString().trim();
    if (!email && !mobile) {
      const message = "At least one of Email or Mobile is required.";
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["email"] });
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["mobile"] });
    }
  });
};

type ClientFormData = z.infer<typeof clientSchemaBase>;

interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onCreate?: (client: any) => void;
  initialData?: any;
  defaultPartnerId?: number | null;
  forceFinalClient?: boolean;
  variant?: "default" | "legacy";
  readOnly?: boolean;
  showClientTypes?: boolean;
}

const ClientForm = ({
  open,
  onOpenChange,
  onSuccess,
  onCreate,
  initialData,
  defaultPartnerId = null,
  forceFinalClient = false,
  variant = "default",
  readOnly = false,
  showClientTypes = true,
}: ClientFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [finalClientTypePicker, setFinalClientTypePicker] = useState<string>("");
  const [partners, setPartners] = useState<Array<{ id: number; name: string; country?: string | null }>>([]);
  const { toast } = useToast();
  const isLegacyVariant = variant === "legacy";
  const isReadOnly = readOnly;
  const validationSchema = buildClientSchema(!isLegacyVariant);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ClientFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialData || {},
  });

  const normalizeClientTypes = (values: string[]) => {
    const map = new Map<string, string>();
    for (const value of values) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!map.has(key)) {
        map.set(key, trimmed);
      }
    }
    return Array.from(map.values());
  };

  const currentClientTypes = watch("client_types") || [];
  const selectedCountry = watch("country") || "";

  const addClientType = (value: string) => {
    const next = normalizeClientTypes([...currentClientTypes, value]);
    setValue("client_types", next, { shouldDirty: true, shouldValidate: true });
    setFinalClientTypePicker("");
  };

  const removeClientType = (value: string) => {
    const next = currentClientTypes.filter((item) => item !== value);
    setValue("client_types", next, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (initialData && open) {
      reset({
        first_name: initialData.first_name,
        last_name: initialData.last_name,
        company: initialData.company || "",
        group_name: "",
        partner_id: defaultPartnerId ?? initialData.partner_id ?? null,
        country: initialData.country || "",
        email: initialData.email,
        mobile: initialData.mobile,
        alternate_phone: initialData.alternate_phone || "",
        operator_agency_name: initialData.operator_agency_name || initialData.operator_agent_name || "",
        referred_from: initialData.referred_from || undefined,
        client_types: Array.isArray(initialData.client_types)
          ? initialData.client_types
          : initialData.final_client_type
            ? [initialData.final_client_type]
            : [],
        language: initialData.language,
        currency: initialData.currency || undefined,
        notes: initialData.notes || "",
      });
    } else if (!initialData && open) {
      reset({
        partner_id: defaultPartnerId,
        country: "",
        client_types: [],
      });
    }
    setFinalClientTypePicker("");
  }, [initialData, open, reset, defaultPartnerId, forceFinalClient, isLegacyVariant]);

  useEffect(() => {
    if (!open || isLegacyVariant) return;
    const loadPartners = async () => {
      try {
        const response = await fetchPartners({ page: 1, size: 2000, sortField: "name", sortOrder: "asc" });
        setPartners(response.items || []);
      } catch {
        setPartners([]);
      }
    };
    loadPartners();
  }, [open, isLegacyVariant]);

  useEffect(() => {
    if (forceFinalClient) {
      setValue("partner_id", null);
    }
    if (typeof defaultPartnerId === "number" || defaultPartnerId === null) {
      setValue("partner_id", defaultPartnerId);
    }
  }, [forceFinalClient, defaultPartnerId, setValue, isLegacyVariant]);

  const onSubmit = async (data: ClientFormData) => {
    if (isReadOnly) return;
    setIsLoading(true);

    try {
      const clientData: any = isLegacyVariant
        ? {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email || null,
            mobile: data.mobile || null,
            alternate_phone: data.alternate_phone || null,
            language: data.language,
            currency: data.currency || null,
            company: data.company || null,
            group_name: data.group_name || null,
            referred_from: data.referred_from || null,
          }
        : {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email || null,
            mobile: data.mobile || null,
            alternate_phone: data.alternate_phone || null,
            partner_id: data.partner_id ?? null,
            country: data.country || null,
            language: data.language,
            company: data.company || null,
            group_name: data.group_name || null,
            operator_agency_name: data.operator_agency_name || null,
            client_types: normalizeClientTypes(data.client_types || []),
            referred_from: data.referred_from || null,
            notes: data.notes || null,
          };

      if (initialData?.id) {
        if (isLegacyVariant) {
          await updateLegacyClient(initialData.id, clientData);
        } else {
          await updateClient(initialData.id, clientData);
        }

        toast({
          title: "Success",
          description: "Client updated successfully",
        });
        onSuccess?.();
      } else {
        const createdClient = isLegacyVariant
          ? await createLegacyClient(clientData)
          : await createClient(clientData);

        toast({
          title: "Success",
          description: "Client created successfully",
        });

        if (createdClient) {
          onCreate?.(createdClient);
        }
        onSuccess?.();
      }

      reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const dialogTitle = isReadOnly
    ? isLegacyVariant
      ? "View Legacy Client"
      : "View Client"
    : initialData
      ? "Edit Client"
      : isLegacyVariant
        ? "Add New Legacy Client"
        : "Add New Client";

  const dialogDescription = isReadOnly
    ? "Client information in read-only mode."
    : "Fill in the client information below. Fields marked with * are required.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                {...register("first_name")}
                placeholder={isReadOnly ? "" : "John"}
                disabled={isReadOnly}
                className={errors.first_name ? "border-destructive focus-visible:ring-destructive" : undefined}
              />
              {errors.first_name && (
                <p className="text-sm text-destructive">{errors.first_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                {...register("last_name")}
                placeholder={isReadOnly ? "" : "Doe"}
                disabled={isReadOnly}
                className={errors.last_name ? "border-destructive focus-visible:ring-destructive" : undefined}
              />
              {errors.last_name && (
                <p className="text-sm text-destructive">{errors.last_name.message}</p>
              )}
            </div>
          </div>

          {!isLegacyVariant && !forceFinalClient && (
            <div className="space-y-2">
              <Label htmlFor="partner_id">Partner</Label>
              <Select
                value={watch("partner_id") == null ? "none" : String(watch("partner_id"))}
                onValueChange={(value) => setValue("partner_id", value === "none" ? null : Number(value))}
                disabled={isReadOnly || typeof defaultPartnerId === "number" || forceFinalClient}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select partner (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Partner (Final Client)</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={String(partner.id)}>
                      {partner.name}{partner.country ? ` (${partner.country})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!isLegacyVariant && showClientTypes && (
            <div className="space-y-2">
              <Label>Final Client Type</Label>
              <SearchableSelect
                items={finalClientTypeOptions.map((option) => ({
                  id: option.value,
                  title: option.label,
                }))}
                value={finalClientTypePicker || undefined}
                onValueChange={(value) => addClientType(String(value))}
                placeholder={isReadOnly ? "" : "Select a final client type"}
                emptyText="No final client type found."
                disabled={isReadOnly}
              />
              {currentClientTypes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {currentClientTypes.map((type) => (
                    <Badge key={type} variant="secondary" className="flex items-center gap-1 pr-1">
                      <span>{finalClientTypeOptions.find((option) => option.value === type)?.label || type}</span>
                      {!isReadOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => removeClientType(type)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
              {!isReadOnly && (
                <p className="text-xs text-muted-foreground">You can add multiple final client types and remove them later.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder={isReadOnly ? "" : "john@example.com"}
                disabled={isReadOnly}
                className={errors.email ? "border-destructive focus-visible:ring-destructive" : undefined}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                {...register("mobile")}
                placeholder={isReadOnly ? "" : "+1 234 567 8900"}
                disabled={isReadOnly}
                className={errors.mobile ? "border-destructive focus-visible:ring-destructive" : undefined}
              />
            </div>
          </div>
          {(errors.email || errors.mobile) && (
            <p className="text-sm text-destructive">
              {errors.email?.message || errors.mobile?.message || "At least one of Email or Mobile is required."}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="alternate_phone">Secondary Phone</Label>
            <Input
              id="alternate_phone"
              {...register("alternate_phone")}
              placeholder={isReadOnly ? "" : "+1 234 567 8901"}
              disabled={isReadOnly}
            />
          </div>


          <div className="grid grid-cols-2 items-start gap-4">
            {!isLegacyVariant && (
              <div className="space-y-2">
                <div className="flex h-6 items-center justify-between">
                  <Label>Country</Label>
                  {selectedCountry && !isReadOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setValue("country", "", { shouldDirty: true, shouldValidate: true })}
                      disabled={isReadOnly}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <SearchableSelect
                  items={COUNTRY_OPTIONS}
                  value={selectedCountry || undefined}
                  onValueChange={(value) => setValue("country", String(value), { shouldDirty: true, shouldValidate: true })}
                  placeholder="Select a country"
                  emptyText="No country found."
                  fallbackDisplayValue={selectedCountry || undefined}
                  disabled={isReadOnly}
                />
                {errors.country && (
                  <p className="text-sm text-destructive">{errors.country.message}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex h-6 items-center">
                <Label htmlFor="language">Language *</Label>
              </div>
              <Select
                value={watch("language")}
                onValueChange={(value) => setValue("language", value as any)}
                disabled={isReadOnly}
              >
                <SelectTrigger className={errors.language ? "border-destructive focus-visible:ring-destructive" : undefined}>
                  <SelectValue placeholder={isReadOnly ? "" : "Select language"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="pt_br">Brazilian Portuguese</SelectItem>
                </SelectContent>
              </Select>
              {errors.language && (
                <p className="text-sm text-destructive">{errors.language.message}</p>
              )}
            </div>

            {isLegacyVariant && (
              <div className="space-y-2">
                <div className="flex h-6 items-center">
                  <Label htmlFor="currency">Currency</Label>
                </div>
                <Select
                  value={watch("currency")}
                  onValueChange={(value) => setValue("currency", value as any)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isReadOnly ? "" : "Select currency"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">US Dollar (USD)</SelectItem>
                    <SelectItem value="aed">UAE Dirham (AED)</SelectItem>
                  </SelectContent>
                </Select>
                {errors.currency && (
                  <p className="text-sm text-destructive">{errors.currency.message}</p>
                )}
              </div>
            )}
          </div>

          {!isLegacyVariant && (
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder={isReadOnly ? "" : "Additional notes about the client..."}
                rows={3}
                disabled={isReadOnly}
              />
            </div>
          )}

          {isLegacyVariant && isReadOnly && (
            <div className="space-y-3 rounded-md border border-border p-4">
              <h4 className="text-sm font-semibold">Legacy Fields</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">ID:</span> {initialData?.id ?? ""}</div>
                <div><span className="text-muted-foreground">Name:</span> {initialData?.name ?? ""}</div>
                <div><span className="text-muted-foreground">Company:</span> {initialData?.company ?? ""}</div>
                <div><span className="text-muted-foreground">Group:</span> {initialData?.group_name ?? ""}</div>
                <div><span className="text-muted-foreground">Email:</span> {initialData?.email ?? ""}</div>
                <div><span className="text-muted-foreground">Mobile:</span> {initialData?.mobile ?? ""}</div>
                <div><span className="text-muted-foreground">Secondary Phone:</span> {initialData?.alternate_phone ?? ""}</div>
                <div><span className="text-muted-foreground">Referred From:</span> {initialData?.referred_from ?? ""}</div>
                <div><span className="text-muted-foreground">Language:</span> {initialData?.language ?? ""}</div>
                <div><span className="text-muted-foreground">Currency:</span> {initialData?.currency ?? ""}</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {isReadOnly ? "Close" : "Cancel"}
            </Button>
            {!isReadOnly && (
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {initialData ? "Update Client" : isLegacyVariant ? "Create Legacy Client" : "Create Client"}
              </Button>
            )}
          </DialogFooter>
        </form>

      </DialogContent>
    </Dialog>
  );
};

export default ClientForm;
